import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationEllipsis,
} from "@ac-expo/ui-components";
import { cn } from "@ui/utils";
import filterStore from "@web-app/src/services/FilterStore";
import { ArrowLeftIcon, ArrowRightIcon } from "../../icons";
import { useMemo } from "react";

type PaginatorProps = {
  pageNumbers: number[];
  changePage: (page: number) => void;
};

const Paginator = ({ pageNumbers, changePage }: PaginatorProps) => {
  const lastPageNumber = pageNumbers.length;
  const currentPage = filterStore.currentPage + 1;
  const isLastPage = currentPage === lastPageNumber;
  const isFirstPage = currentPage === 1;

  const firstPaginationNumbers = useMemo(() => {
    if (currentPage < 5) {
      return Array.from(
        { length: Math.min(5, lastPageNumber) },
        (_, i) => i + 1
      );
    } else {
      return [1];
    }
  }, [pageNumbers, currentPage]);

  const centerPaginationNumbers = useMemo(() => {
    if (currentPage > 4 && currentPage <= lastPageNumber - 3) {
      return [currentPage - 1, currentPage, currentPage + 1];
    } else {
      return [];
    }
  }, [pageNumbers, currentPage, lastPageNumber]);

  const lastPaginationNumbers = useMemo(() => {
    if (currentPage > lastPageNumber - 3 && lastPageNumber > 5) {
      const allLastNumbers = Array.from(
        { length: 5 },
        (_, i) => lastPageNumber - i
      ).reverse();

      return allLastNumbers.filter(
        (number) => !firstPaginationNumbers.includes(number)
      );
    } else if (lastPageNumber > 5) {
      return [lastPageNumber];
    } else {
      return [];
    }
  }, [pageNumbers, currentPage, lastPageNumber, firstPaginationNumbers]);

  return (
    <Pagination className="flex items-center justify-center p-1 md:justify-end">
      <button
        onClick={() => changePage(currentPage - 1)}
        className={cn("gap-1 rounded-l-xl border px-2.5 py-[7px]", {
          "cursor-not-allowed opacity-50": isFirstPage,
        })}
        disabled={isFirstPage}
      >
        <ArrowLeftIcon width={20} height={20} fill="#287BB9" />
      </button>
      <PaginationContent>
        {firstPaginationNumbers.map((number: number) => (
          <PaginationItem key={number} className="border-none">
            <PaginationLink
              onClick={() => changePage(number)}
              isActive={currentPage === number}
              className="border"
            >
              {number}
            </PaginationLink>
          </PaginationItem>
        ))}
        {centerPaginationNumbers.length > 0 && (
          <PaginationItem className="border-none">
            <PaginationEllipsis className="border-none" />
          </PaginationItem>
        )}
        {centerPaginationNumbers.map((number: number) => (
          <PaginationItem key={number} className="border-none">
            <PaginationLink
              onClick={() => changePage(number)}
              isActive={currentPage === number}
              className="border"
            >
              {number}
            </PaginationLink>
          </PaginationItem>
        ))}
        {lastPaginationNumbers.length > 0 &&
          firstPaginationNumbers.at(-1) !== lastPaginationNumbers[0] - 1 && (
            <PaginationItem className="border-none">
              <PaginationEllipsis className="border-none" />
            </PaginationItem>
          )}
        {lastPaginationNumbers.map((number: number) => (
          <PaginationItem key={number} className="border-none">
            <PaginationLink
              onClick={() => changePage(number)}
              isActive={currentPage === number}
              className="border"
            >
              {number}
            </PaginationLink>
          </PaginationItem>
        ))}
      </PaginationContent>
      <button
        onClick={() => changePage(currentPage + 1)}
        className={cn(
          "flex items-center justify-center gap-1 rounded-r-xl border px-2.5 py-[7px]",
          {
            "cursor-not-allowed opacity-50": isLastPage,
          }
        )}
        disabled={isLastPage}
      >
        <ArrowRightIcon width={20} height={20} fill="#287BB9" />
      </button>
    </Pagination>
  );
};

export default Paginator;
